import React, { useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { FormProvider, useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import { withTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import useSWR from 'swr'
import * as UIcons from '@iconscout/react-unicons'
import Btn, { FormBtn } from 'components/Btn'
import Input from 'components/Input'
import PhoneNumberInput from 'components/PhoneNumberInput'
import { apiRequest } from 'store/Misc/apiRequest'
import { setFormLoading } from 'store/slices/miscSlice'
import { filterValues, destructureErrorMessage } from 'Helper'
import { UserValidation } from 'pages/Settings/validation'

export default withTranslation()(function UserForm({ t, user, handleClick, onSaved }) {
  const dispatch = useDispatch()
  const { data: rolesRes } = useSWR('user-mgmt/roles')
  const { data: branchesRes } = useSWR('merchants/branches')
  const [avatarPreview, setAvatarPreview] = useState(user?.avatar ?? null)

  const roles = rolesRes?.data ?? []
  const branches = branchesRes?.data ?? []

  const method = useForm({ mode: 'all', resolver: yupResolver(UserValidation) })
  const { reset, handleSubmit, setValue, formState: { isValid, isDirty } } = method

  useEffect(() => {
    if (user) {
      reset({
        first_name: user.first_name,
        last_name: user.last_name,
        email: user.email,
        phone: user.phone,
        job_title: user.job_title,
        branch_id: user.branch_id,
        role_id: user.roles?.[0]?.id
      })
    }
  }, [user, reset])

  const handleAvatarChange = (fileList) => {
    const file = fileList?.[0]
    if (!file) return
    setAvatarPreview(URL.createObjectURL(file))
    setValue('avatar', file)
  }

  const submit = async (payload) => {
    dispatch(setFormLoading())
    try {
      await apiRequest({
        method: user ? 'put' : 'post',
        url: user ? `user-mgmt/users/${user.id}` : 'merchants/users',
        payload
      })

      toast.success(t(user ? 'settings.user_management.success_update' : 'settings.user_management.success_create'))
      onSaved && onSaved()
      handleClick()
    } catch ({ message }) {
      destructureErrorMessage(message).map((msg) => toast.error(msg, { autoClose: 15000 }))
    } finally {
      dispatch(setFormLoading('close'))
    }
  }

  return (
    <div className="fixed inset-0 z-[5000] bg-black bg-opacity-30 flex items-center justify-center p-4" onClick={handleClick}>
      <div className="bg-white relative w-11/12 max-w-md rounded-xl max-h-[90vh] flex flex-col overflow-hidden" onClick={(event) => event.stopPropagation()}>
        <div className="p-6 md:p-8 pb-0 shrink-0">
          <div className="relative flex items-center justify-center mb-6">
            <Btn
              className="w-10 h-10 hover:bg-default absolute left-0"
              content={() => <UIcons.UilTimes className="text-secondary" />}
              click={handleClick}
            />

            <h3 className="text-xl font-bold text-dark-blue">
              {t(user ? 'settings.user_management.edit_title' : 'settings.user_management.add_title')}
            </h3>
          </div>

          <div className="w-full h-1.5 bg-primary rounded-full mb-6" />
        </div>

        <div className="overflow-y-auto px-6 md:px-8 pb-6 md:pb-8">
          <FormProvider {...method}>
            <form onSubmit={handleSubmit(submit)}>
            {
              user && (
                <div className="flex justify-center mb-6">
                  <div className="relative w-20 h-20">
                    {
                      avatarPreview ? (
                        <img src={avatarPreview} alt="" className="w-20 h-20 rounded-full object-cover" />
                      ) : (
                        <div className="w-20 h-20 rounded-full bg-icon-bg flex items-center justify-center">
                          <UIcons.UilUser className="text-primary" size={28} />
                        </div>
                      )
                    }

                    <button
                      type="button"
                      className="absolute -bottom-1 -right-1 w-7 h-7 rounded-full bg-white border border-border-lines shadow flex items-center justify-center"
                      onClick={() => document.getElementById('user-avatar-input')?.click()}
                    >
                      <UIcons.UilPen size={12} className="text-primary" />
                    </button>

                    <input
                      type="file"
                      id="user-avatar-input"
                      className="hidden"
                      accept="image/*"
                      onChange={(event) => handleAvatarChange(event.target.files)}
                    />
                  </div>
                </div>
              )
            }

            <div className="grid md:grid-cols-2 gap-5 mb-3">
              <Input
                name="first_name"
                label={t('settings.user_management.form.first_name.label')}
                placeholder={t('settings.user_management.form.first_name.placeholder')}
                variant="outlined"
              />

              <Input
                name="last_name"
                label={t('settings.user_management.form.last_name.label')}
                placeholder={t('settings.user_management.form.last_name.placeholder')}
                variant="outlined"
              />
            </div>

            <div className="mb-3">
              <Input
                name="email"
                label={t('settings.user_management.form.work_email.label')}
                placeholder={t('settings.user_management.form.work_email.placeholder')}
                variant="outlined"
              />
            </div>

            <div className="mb-3">
              <PhoneNumberInput
                name="phone"
                label={t('settings.user_management.form.phone.label')}
                className={{}}
                singleInputClassName="w-full bg-white rounded-lg py-3"
                country="NG"
                code="234"
              />
            </div>

            <div className="mb-3">
              <Input
                name="job_title"
                label={t('settings.user_management.form.job_title.label')}
                placeholder={t('settings.user_management.form.job_title.placeholder')}
                variant="outlined"
              />
            </div>

            <div className="mb-3">
              <Input
                name="role_id"
                label={t('settings.user_management.form.assign_role.label')}
                placeholder={t('settings.user_management.form.assign_role.placeholder')}
                variant="outlined"
                options={filterValues({ options: roles, param: { value: 'id', label: 'display_name' } })}
              />
            </div>

            <div className="mb-6">
              <Input
                name="branch_id"
                label={t('settings.user_management.form.assign_branch.label')}
                placeholder={t('settings.user_management.form.assign_branch.placeholder')}
                variant="outlined"
                options={filterValues({ options: branches, param: { value: 'id', label: 'name' } })}
              />
            </div>

            <FormBtn
              content={t(user ? 'misc.save' : 'settings.user_management.add_title')}
              className="w-full h-12 rounded-xl bg-primary text-white disabled:opacity-40"
              disabled={!isValid || !isDirty}
            />
            </form>
          </FormProvider>
        </div>
      </div>
    </div>
  )
})
